// ============================================================
// signup-email-check Stimulus 컨트롤러 (2026-05-14)
// ============================================================
// 역할: /signup 페이지의 이메일 중복확인 UX 처리
//   1) "중복확인" 버튼 클릭 → fetch POST /signup/check_email
//   2) 응답에 따라 상태 영역(사용 가능 / 이미 사용중 / 오류) 표시
//   3) "사용 가능" 일 때만 가입 버튼(submitBtn) 활성화
//   4) 이메일 인풋이 수정되면 다시 미확인 상태로 되돌리며 가입 버튼 재차 비활성화
//
// 대상 (data-signup-email-check-target):
//   - emailInput : 이메일 입력 필드
//   - checkBtn   : "중복확인" 버튼
//   - status     : 결과 표시 영역 (사용 가능/이미 사용중/오류 메시지)
//   - submitBtn  : 가입 폼 제출 버튼 (초기 disabled)
//
// 액션:
//   - click->signup-email-check#check       : 중복확인 호출
//   - input->signup-email-check#onEmailChange: 이메일 수정 감지 → 검증 상태 리셋
//
// 값 (data-signup-email-check-*-value):
//   - checkUrl : POST 호출 대상 URL (서버 routes 에서 주입한 signup_check_email_path)
// ============================================================

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // 타깃과 값 선언
  static targets = ["emailInput", "checkBtn", "status", "submitBtn"]
  static values  = { checkUrl: String }

  // 컨트롤러 초기화 시 가입 버튼 비활성 보장 (서버 측에서 이미 disabled 마킹했지만 안전성 차원)
  connect() {
    this._setAvailable(false)
  }

  // ─────────────────────────────────────────────────────────
  // 중복확인 버튼 클릭 핸들러
  // ─────────────────────────────────────────────────────────
  async check(event) {
    event.preventDefault()

    const email = this.emailInputTarget.value.trim().toLowerCase()
    // 클라이언트 측 1차 검증 — 빈 값/형식 오류는 즉시 안내
    if (!email) {
      this._renderStatus("error", this.i18n("empty"))
      return
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      this._renderStatus("error", this.i18n("invalid"))
      return
    }

    // 호출 중 UX — 버튼 잠금 + 로딩 텍스트
    this.checkBtnTarget.disabled = true
    const originalLabel = this.checkBtnTarget.textContent
    this.checkBtnTarget.textContent = this.i18n("checking")

    try {
      // CSRF 토큰 — Rails 의 기본 메타 태그에서 추출 (form_with 가 자동 삽입)
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""

      const response = await fetch(this.checkUrlValue, {
        method: "POST",
        headers: {
          "Content-Type":   "application/json",
          "Accept":         "application/json",
          "X-CSRF-Token":   csrf
        },
        body: JSON.stringify({ email })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        // 422 (형식 오류) / 502 (A서버 통신 실패) 등 — 서버 메시지 표시
        const msg = data.message || this.i18n("server_error")
        this._renderStatus("error", msg)
        this._setAvailable(false)
        return
      }

      // 정상 응답: { ok: true, available: true|false }
      if (data.ok && data.available === true) {
        this._renderStatus("success", this.i18n("available"))
        this._setAvailable(true)
      } else if (data.ok && data.available === false) {
        this._renderStatus("error", this.i18n("duplicate"))
        this._setAvailable(false)
      } else {
        this._renderStatus("error", data.message || this.i18n("server_error"))
        this._setAvailable(false)
      }
    } catch (err) {
      // 네트워크 끊김 등 fetch 자체 실패
      this._renderStatus("error", this.i18n("network_error"))
      this._setAvailable(false)
    } finally {
      this.checkBtnTarget.disabled = false
      this.checkBtnTarget.textContent = originalLabel
    }
  }

  // ─────────────────────────────────────────────────────────
  // 이메일 인풋 수정 핸들러 — 검증 상태 리셋
  // ─────────────────────────────────────────────────────────
  // 한 번 "사용 가능" 통과한 뒤 사용자가 이메일을 바꾸면 검증을 다시 받게 한다.
  // 안전성: 클라이언트 우회와 무관하게 서버측 안전망(POST /signup) 도 다시 검사.
  onEmailChange() {
    if (this.statusTarget && !this.statusTarget.classList.contains("hidden")) {
      this.statusTarget.classList.add("hidden")
      this.statusTarget.textContent = ""
    }
    this._setAvailable(false)
  }

  // ─────────────────────────────────────────────────────────
  // 내부 헬퍼: 상태 표시 영역 갱신
  // ─────────────────────────────────────────────────────────
  // type: "success" | "error"
  _renderStatus(type, message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.classList.remove("hidden")
    this.statusTarget.textContent = message
    // 색은 디자인 토큰 활용 — 성공: 시안 네온, 실패: 적색
    if (type === "success") {
      this.statusTarget.style.color = "var(--neon-primary)"
    } else {
      this.statusTarget.style.color = "var(--semantic-danger)"
    }
  }

  // ─────────────────────────────────────────────────────────
  // 내부 헬퍼: 가입 버튼 활성/비활성 토글
  // ─────────────────────────────────────────────────────────
  _setAvailable(isAvailable) {
    if (!this.hasSubmitBtnTarget) return
    this.submitBtnTarget.disabled = !isAvailable
    // 시각 단서: 비활성 시 흐릿하게
    this.submitBtnTarget.style.opacity = isAvailable ? "1" : "0.55"
    this.submitBtnTarget.style.cursor  = isAvailable ? "pointer" : "not-allowed"
  }

  // ─────────────────────────────────────────────────────────
  // 내부 헬퍼: i18n 메시지
  // ─────────────────────────────────────────────────────────
  // i18n 처리는 서버 측에서만 진행하지만, fetch 호출 직전/직후의 즉각 피드백은
  // 클라이언트 단의 영문/한글 정적 메시지로 충분. 만약 6개 언어 정밀 매칭 필요하면
  // 향후 data-* 속성으로 서버 i18n 문자열을 주입하도록 확장 가능.
  // 현재는 한글 단일 메시지 (signup 페이지의 주 사용 언어).
  i18n(key) {
    const messages = {
      empty:         "이메일을 입력해 주세요.",
      invalid:       "올바른 이메일 형식이 아닙니다.",
      checking:      "확인 중...",
      available:     "✓ 사용 가능한 이메일입니다.",
      duplicate:     "✗ 이미 사용 중인 이메일입니다.",
      server_error:  "확인에 실패했습니다. 잠시 후 다시 시도해 주세요.",
      network_error: "네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요."
    }
    return messages[key] || key
  }
}
